/**
 * Premium /getintouch page content — proof metrics, channels, project types, next steps, routes.
 */

export type ContactProofMetric = {
  id: string;
  value: string;
  label: string;
  detail: string;
};

export type ContactChannel = {
  id: string;
  title: string;
  detail: string;
  href?: string;
  external?: boolean;
};

export type ContactProjectType = {
  id: string;
  label: string;
};

export type ContactNextStep = {
  id: string;
  step: string;
  title: string;
  detail: string;
};

export type ContactServiceRoute = {
  id: string;
  title: string;
  summary: string;
  href: string;
  label: string;
};

export const HERO_RESPONSE_NODES = [
  '24–48h reply',
  'Gautam Buddha Nagar team',
  'Global delivery',
  'Web & ecommerce',
  'CRM & forms',
  'Maintenance',
  'AI automation',
  'Support',
];

export const CONTACT_PROOF_METRICS: ContactProofMetric[] = [
  {
    id: 'response',
    value: '24–48 hours',
    label: 'typical response',
    detail: 'We review every serious enquiry and respond with next steps or clarifying questions.',
  },
  {
    id: 'team',
    value: 'Gautam Buddha Nagar, Uttar Pradesh',
    label: 'senior-led team',
    detail: 'Production-focused engineers for web, ecommerce, integrations, and long-term support.',
  },
  {
    id: 'coverage',
    value: 'Full-stack',
    label: 'service coverage',
    detail: 'Web development, Magento/WooCommerce, WordPress, CRM, analytics, AI, and maintenance.',
  },
  {
    id: 'hours',
    value: 'Mon–Sat',
    label: 'support hours',
    detail: '9am–6pm IST for email and chat support. We do not take phone calls.',
  },
];

export const CONTACT_CHANNELS: ContactChannel[] = [
  {
    id: 'office',
    title: 'PLUX INFOSYSTEMS PVT. LTD.',
    detail: '135, Rajpuram, Chipiyana Buzurg, Gautam Buddha Nagar, Uttar Pradesh 201009',
  },
  {
    id: 'email',
    title: 'info@pluxinfosystems.com',
    detail: 'Online project support',
    href: 'mailto:info@pluxinfosystems.com',
  },
  {
    id: 'chat',
    title: 'Chat on this site',
    detail: 'Bottom-right chat — email or chat only, no calls',
  },
];

export const CONTACT_PROJECT_TYPES: ContactProjectType[] = [
  { id: 'web', label: 'Web development' },
  { id: 'ecommerce', label: 'Ecommerce' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'maintenance', label: 'Maintenance' },
  { id: 'ai', label: 'AI automation' },
  { id: 'wordpress', label: 'WordPress / Magento' },
];

export const CONTACT_NEXT_STEPS: ContactNextStep[] = [
  {
    id: 'review',
    step: '01',
    title: 'We review your brief',
    detail: 'Stack, timeline, goals, and any existing site or platform context.',
  },
  {
    id: 'call',
    step: '02',
    title: 'Technical discovery call',
    detail: 'A short call to clarify scope, integrations, and delivery approach.',
  },
  {
    id: 'estimate',
    step: '03',
    title: 'Estimate or roadmap',
    detail: 'Clear next steps, effort range, or phased delivery plan.',
  },
  {
    id: 'kickoff',
    step: '04',
    title: 'Delivery kickoff',
    detail: 'On approval, we align on milestones, access, and communication cadence.',
  },
];

export const CONTACT_SERVICE_ROUTES: ContactServiceRoute[] = [
  {
    id: 'web',
    title: 'Web development',
    summary: 'React, Next.js, Laravel, product engineering, and landing pages.',
    href: '/web-development',
    label: 'Web development services',
  },
  {
    id: 'ecommerce',
    title: 'Ecommerce development',
    summary: 'Magento, WooCommerce, checkout flows, and marketplace integrations.',
    href: '/ecommerce-development',
    label: 'Ecommerce services',
  },
  {
    id: 'integrations',
    title: 'CRM & form integrations',
    summary: 'Salesforce, HubSpot, Marketo, FormAssembly, and lead workflows.',
    href: '/form-crm-integrations',
    label: 'Integration services',
  },
  {
    id: 'maintenance',
    title: 'Website maintenance',
    summary: 'Performance, security, Core Web Vitals, and ongoing support.',
    href: '/website-maintenance',
    label: 'Maintenance services',
  },
  {
    id: 'services',
    title: 'All services',
    summary: 'Full service directory including AI automation and WordPress.',
    href: '/services',
    label: 'View all services',
  },
  {
    id: 'insights',
    title: 'Delivery insights',
    summary: 'Guides on web, ecommerce, CRM, analytics, and CMS delivery.',
    href: '/blog',
    label: 'Browse insights',
  },
];
